import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "../lib/apiClient";
import { useCart } from "../store/cart";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", payment: null, orderId: null, amount: null });
  const attemptsRef = useRef(0);
  const { clear } = useCart();

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", payment: null });
      return;
    }
    let stopped = false;

    const poll = async () => {
      if (stopped) return;
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          clear();
          setState({ status: "paid", payment: data, orderId: data.order_id, amount: data.amount });
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setState({ status: "failed", payment: data });
          return;
        }
        if (attemptsRef.current >= 8) {
          setState({ status: "timeout", payment: data });
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        setState({ status: "error" });
      }
    };
    poll();
    return () => { stopped = true; };
  }, [sessionId, clear]);

  return (
    <div data-testid="checkout-success-page" className="mx-auto max-w-3xl px-4 py-24 text-center">
      {state.status === "checking" && (
        <>
          <Loader2 size={48} className="text-blood animate-spin mx-auto" />
          <p className="font-anton text-bone text-4xl uppercase tracking-tighter mt-6">VERIFYING PAYMENT</p>
          <p className="text-ash text-sm mt-2 tracking-[0.2em]">// HOLD STILL, REBEL</p>
        </>
      )}
      {state.status === "paid" && (
        <>
          <CheckCircle2 size={64} className="text-blood mx-auto" />
          <p className="text-blood text-xs tracking-[0.3em] font-mono mt-6">// PAYMENT CONFIRMED</p>
          <h1 className="font-anton uppercase text-bone text-5xl sm:text-7xl tracking-tighter mt-2">
            YOU'RE <span className="text-blood">IN</span>.
          </h1>
          <p className="font-marker text-bone text-3xl mt-4">pain is temporary. legacy is forever.</p>
          <div className="mt-8 inline-block border border-white/15 p-6 text-left">
            <div className="text-[11px] tracking-[0.2em] uppercase text-ash">// ORDER ID</div>
            <div data-testid="success-order-id" className="font-mono text-bone text-sm break-all">{state.orderId}</div>
            <div className="text-[11px] tracking-[0.2em] uppercase text-ash mt-3">// TOTAL</div>
            <div className="font-anton text-bone text-2xl">${Number(state.amount).toFixed(2)}</div>
          </div>
          <p className="text-ash text-sm mt-6">CONFIRMATION SENT. KEEP YOUR ORDER ID — TRACK ANYTIME.</p>
          <div className="mt-8 flex gap-3 justify-center">
            <Link to="/shop" data-testid="success-shop-more" className="px-6 py-3 border border-bone text-bone text-xs tracking-[0.3em] uppercase hover:bg-bone hover:text-ink">SHOP MORE</Link>
            <Link to="/order-lookup" data-testid="success-track-order" className="px-6 py-3 bg-blood text-bone text-xs tracking-[0.3em] uppercase hover:bg-bone hover:text-ink">TRACK ORDER</Link>
          </div>
        </>
      )}
      {(state.status === "failed" || state.status === "error") && (
        <>
          <XCircle size={56} className="text-blood mx-auto" />
          <h1 className="font-anton text-bone text-5xl uppercase tracking-tighter mt-6">PAYMENT FAILED</h1>
          <p className="text-ash text-sm mt-4">Something broke. Try again — discipline.</p>
          <Link to="/cart" data-testid="back-to-cart-link" className="inline-block mt-8 px-6 py-3 bg-blood text-bone text-xs tracking-[0.3em] uppercase">BACK TO BAG</Link>
        </>
      )}
      {state.status === "timeout" && (
        <>
          <p className="font-anton text-bone text-4xl uppercase tracking-tighter">STILL PROCESSING</p>
          <p className="text-ash text-sm mt-4">It's taking longer than usual. Check email or track later.</p>
          <Link to="/order-lookup" data-testid="timeout-track-link" className="inline-block mt-8 px-6 py-3 bg-blood text-bone text-xs tracking-[0.3em] uppercase">TRACK ORDER</Link>
        </>
      )}
    </div>
  );
}
